import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../../config/URL/URL';
import {
    CalculatorContent,
    InputContainer,
    Label,
    InputWrapper,
    UnitText,
    StyledSelect,
    ResultDisplay,
    ResultTitle,
    ResultContent,
    ResultRow,
    ResultLabel,
    ResultValue,
    ResultHighlight,
    ResultNote,
} from './styles';
import CommonButton from '../button/CommonButton';
import { css } from '@emotion/react';
import { calculateCommission } from '../../../utils/calculator/calculateCommission';
import { handleNumberInputChange, getNumericValue } from '../../../utils/calculator/numberFormatter';

const CommissionCalculator = ({ checkModalBoundary, adjustModalPosition }) => {
    const [transactionType, setTransactionType] = useState('');
    const [amount, setAmount] = useState('');
    const [result, setResult] = useState(null);
    const [monthlyRent, setMonthlyRent] = useState('');
    const [selectedSido, setSelectedSido] = useState(null);
    const [sidoList, setSidoList] = useState([]);
    const [shouldCheckBoundary, setShouldCheckBoundary] = useState(false);

    const handleAmountChange = (e) => {
        handleNumberInputChange(e, setAmount, 6);
    };

    const handleMonthlyRentChange = (e) => {
        handleNumberInputChange(e, setMonthlyRent, 5);
    };

    useEffect(() => {
        fetchSidoList();
    }, []);

    const fetchSidoList = async () => {
        try {
            const response = await axios.get(`${API_URL}/sodamap-api/sido_list`);
            const data = response.data;
            data.sort((a, b) => parseInt(a.sidoCd) - parseInt(b.sidoCd));
            setSidoList(data);
        } catch (error) {
            console.log(error);
        }
    };

    const handleSidoChange = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue) {
            const sido = sidoList.find((item) => item.sidoNm === selectedValue);
            setSelectedSido(sido);
        } else {
            setSelectedSido(null);
        }
    };

    useEffect(() => {
        if (shouldCheckBoundary && result) {
            const timer = setTimeout(() => {
                if (checkModalBoundary()) {
                    adjustModalPosition();
                }
                setShouldCheckBoundary(false);
            }, 0);

            return () => clearTimeout(timer);
        }
    }, [result, shouldCheckBoundary, checkModalBoundary, adjustModalPosition]);

    const handleCalculate = () => {
        if (!transactionType || !amount || !selectedSido) {
            alert('모든 필드를 입력해주세요.');
            return;
        }

        const amountValue = getNumericValue(amount);
        const monthlyRentValue = getNumericValue(monthlyRent);

        // 음수 체크
        if (amountValue < 0) {
            alert('보증금액은 음수일 수 없습니다.');
            return;
        }

        if (transactionType === '월세' && monthlyRentValue < 0) {
            alert('월세는 음수일 수 없습니다.');
            return;
        }

        // 비현실적으로 큰 금액 체크 (1000억원 이상)
        if (amountValue >= 10000000) {
            alert('보증금액이 너무 큽니다. 다시 확인해주세요.');
            return;
        }

        if (transactionType === '월세' && monthlyRentValue > 10000) {
            alert('월세가 너무 큽니다. 다시 확인해주세요.');
            return;
        }

        const calculatedResult = calculateCommission(transactionType, amountValue, monthlyRentValue);

        setResult(calculatedResult);
        setShouldCheckBoundary(true);
    };

    const clearAll = () => {
        setAmount('');
        setTransactionType('');
        setSelectedSido(null);
        setResult(null);
    };

    return (
        <CalculatorContent>
            <InputContainer>
                <Label>
                    <span>지역</span>
                    <span>*</span>
                </Label>
                <StyledSelect
                    value={selectedSido ? selectedSido.sidoNm : ''}
                    onChange={handleSidoChange}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <option value="">시도 선택</option>
                    {sidoList.map((sido) => (
                        <option key={sido.gid} value={sido.sidoNm}>
                            {sido.sidoNm}
                        </option>
                    ))}
                </StyledSelect>
            </InputContainer>

            <InputContainer>
                <Label>
                    <span>거래유형</span>
                    <span>*</span>
                </Label>
                <StyledSelect
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <option value="">선택해주세요</option>
                    <option value="매매">매매</option>
                    <option value="전세">전세</option>
                    <option value="월세">월세</option>
                </StyledSelect>
            </InputContainer>

            <InputContainer>
                <Label>
                    <span>{transactionType === '매매' ? '거래금액' : '보증금액'}</span>
                    <span>*</span>
                </Label>
                <InputWrapper>
                    <input
                        type="text"
                        value={amount}
                        onChange={handleAmountChange}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <UnitText>만원</UnitText>
                </InputWrapper>
                {transactionType === '월세' && (
                    <>
                        <Label>
                            <span>월세</span>
                        </Label>
                        <InputWrapper>
                            <input
                                type="text"
                                value={monthlyRent}
                                onChange={handleMonthlyRentChange}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <UnitText>만원</UnitText>
                        </InputWrapper>
                    </>
                )}
            </InputContainer>

            <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                <CommonButton
                    variant="secondary"
                    size="medium"
                    onClick={handleCalculate}
                    customStyles={css`
                        flex: 1;
                        border: none;

                        &:hover {
                            transform: translateY(-2px);
                        }
                    `}
                >
                    수수료 계산하기
                </CommonButton>

                <CommonButton
                    variant="outlined"
                    size="medium"
                    onClick={clearAll}
                    customStyles={css`
                        flex: 1;
                    `}
                >
                    초기화
                </CommonButton>
            </div>

            {result && (
                <ResultDisplay>
                    <ResultTitle>계산 결과</ResultTitle>
                    <ResultContent>
                        <ResultRow>
                            <ResultLabel>상한요율</ResultLabel>
                            <ResultValue>{result.commissionRate}%</ResultValue>
                        </ResultRow>
                        <ResultRow>
                            <ResultLabel>한도액</ResultLabel>
                            <ResultValue>
                                {result.commissionLimit ? `${result.commissionLimit.toLocaleString()}원` : '없음'}
                            </ResultValue>
                        </ResultRow>
                        <ResultRow>
                            <ResultLabel>중개수수료</ResultLabel>
                            <ResultHighlight>{result.commission.toLocaleString()}원</ResultHighlight>
                        </ResultRow>
                    </ResultContent>
                    <ResultNote>해당 결과는 참고용으로, 실제 세액과 차이가 있을 수 있습니다.</ResultNote>
                </ResultDisplay>
            )}
        </CalculatorContent>
    );
};

export default CommissionCalculator;
